/** First name, optional middle name, last name — mirrors `PersonNameRules.MaxNameParts` on the API. */
export const maxNameParts = 3;

/**
 * Upper-case the first letter of each segment of a single name word and lower-case the rest, so
 * compound names survive ("o'BRIEN" -> "O'Brien", "mary-jane" -> "Mary-Jane").
 */
const capitalizeWord = (word: string): string =>
	word
		.toLocaleLowerCase()
		.replace(
			/(^|[-'.])(\p{L})/gu,
			(_match: string, separator: string, letter: string) => separator + letter.toLocaleUpperCase()
		);

/** Split a typed name into its whitespace-separated parts. */
export const splitNameParts = (value: string): string[] => value.trim().split(/\s+/u).filter(Boolean);

/**
 * True when the name has more parts than "first, middle, last". Such a name is rejected rather than
 * reformatted — silently dropping a part the user typed would be worse than telling them.
 */
export const hasTooManyNameParts = (value: string): boolean => splitNameParts(value).length > maxNameParts;

/**
 * Normalize a person's display name to the canonical "Firstname M Lastname" shape, whatever the user
 * typed: runs of whitespace collapse to one space, every part is capitalized, and the middle name is
 * reduced to a single initial ("  aashvi  KUMARI verma " -> "Aashvi K Verma").
 *
 * This mirrors `StringExtensions.ToPersonName` on the API, which is the authority — this copy lets the
 * user see the canonical form while they type instead of after they save.
 */
export const formatPersonName = (value: string): string => {
	const parts = splitNameParts(value);

	if (parts.length === 0) {
		return "";
	}

	const firstName = capitalizeWord(parts[0]);

	if (parts.length === 1) {
		return firstName;
	}

	const lastName = capitalizeWord(parts[parts.length - 1]);

	if (parts.length === 2) {
		return `${firstName} ${lastName}`;
	}

	// Anything past three parts is rejected by validation; a clamped name keeps one middle initial.
	return `${firstName} ${capitalizeWord(parts[1]).slice(0, 1)} ${lastName}`;
};
